import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, UserCog, Crown, X, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

interface UserFiltersProps {
  onSearchChange: (value: string) => void;
  onFilterChange: (value: 'all' | 'super_admin' | 'manager') => void;
  filterValue: 'all' | 'super_admin' | 'manager';
  onAddUser?: () => void;
}

export function UserFilters({
  onSearchChange,
  onFilterChange,
  filterValue,
  onAddUser,
}: UserFiltersProps) {
  // Dropdown açık/kapalı durumu
  const [open, setOpen] = useState(false);

  // Filtreleri sıfırla ve menüyü kapat
  const resetFilters = () => {
    onFilterChange('all');
    setOpen(false);
  };

  // Herhangi bir filtre aktif mi?
  const isAnyFilterActive = filterValue !== 'all';

  // Tıklama işleyicileri - değeri değiştir ve menüyü kapat
  const handleFilterChange = (value: 'all' | 'super_admin' | 'manager') => {
    onFilterChange(value);
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="space-y-4">
        {/* Arama ve Filtre */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="İsim veya email ile arayın..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${isAnyFilterActive ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100" : ""}`}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filtrele
                {isAnyFilterActive && (
                  <span className="ml-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    1
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2">
              {/* Rol Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Kullanıcı Rolü
              </div>
              <DropdownMenuItem
                onClick={() => handleFilterChange(filterValue === 'super_admin' ? 'all' : 'super_admin')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${filterValue === 'super_admin' ? 'bg-purple-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Crown className={`mr-3 h-4 w-4 ${filterValue === 'super_admin' ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className={filterValue === 'super_admin' ? 'font-medium text-purple-700' : 'text-slate-700'}>Süper Adminler</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleFilterChange(filterValue === 'manager' ? 'all' : 'manager')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${filterValue === 'manager' ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <UserCog className={`mr-3 h-4 w-4 ${filterValue === 'manager' ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className={filterValue === 'manager' ? 'font-medium text-green-700' : 'text-slate-700'}>Yöneticiler</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {onAddUser && (
            <Button
              onClick={onAddUser}
              className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Yeni Kullanıcı Ekle
            </Button>
          )}
        </div>

        {/* Aktif filtre belirteçleri */}
        {isAnyFilterActive && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <span className="text-xs font-medium text-slate-600 flex items-center">
              Aktif Filtreler:
            </span>
            {filterValue === 'super_admin' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-800 border-0 hover:bg-purple-200">
                <Crown className="h-3 w-3" />
                Süper Adminler
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-purple-600"
                  onClick={() => onFilterChange('all')}
                />
              </Badge>
            )}

            {filterValue === 'manager' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                <UserCog className="h-3 w-3" />
                Yöneticiler
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                  onClick={() => onFilterChange('all')}
                />
              </Badge>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
            >
              Tümünü Temizle
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
